import React, { useEffect, useRef, useState } from "react";
import { X, Send, Loader2, RotateCcw, ClipboardPlus } from "lucide-react";
import { API, api } from "@/lib/api";
import { Bubble, MascotAvatar, MascotHero, MascotLauncher } from "./MascotParts";
import { MASCOT_NAME, MASCOT_THUMBS } from "./mascotScript";

const SID_KEY = "amc-school-mascot-sid";
const newSid = () => { const s = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(SID_KEY, s); return s; };
const visible = (t) => (t || "").split("[[")[0].replace(/R\$\s?[\d.,]+|\b[\d.,]+\s?(?:mil\s)?reais\b/gi, "[valor no orçamento]");
const HELLO = { role: "bot", text: "Oi! Eu sou o Maurilho, da AMC. Posso ver pra você como estão os orçamentos, o andamento dos serviços e as visitas agendadas. Também abro um pedido de serviço novo. O que você precisa?" };
const SUGGESTIONS = ["Como estão meus orçamentos?", "O que falta terminar?", "Quando a equipe vem?", "Quero pedir um serviço"];

const Proposal = ({ p, msgId, onSent }) => {
  const [state, setState] = useState(p.request_id ? "sent" : "idle");
  const send = async () => {
    setState("sending");
    try { await api.post(`/school/mascot/proposal/${msgId}/confirm`); setState("sent"); onSent(p.title); }
    catch { setState("idle"); }
  };
  return (
    <div className="ml-9 rounded-2xl border-2 border-dashed border-[#7c3aed]/50 bg-violet-50 p-3 text-[12.5px]" data-testid="school-mascot-proposal">
      <p className="font-black text-[#0f172a]">{p.title}</p>
      {p.description && <p className="text-slate-600 mt-0.5">{p.description}</p>}
      {state === "sent" ? <p className="mt-2 font-bold text-emerald-700" data-testid="school-mascot-proposal-sent">Solicitação enviada para a AMC ✓</p> : (
        <button type="button" onClick={send} disabled={state === "sending"} data-testid="school-mascot-proposal-confirm"
          className="mt-2 h-9 px-3 rounded-xl bg-[#7c3aed] text-white font-black inline-flex items-center gap-1.5 disabled:opacity-60">
          {state === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardPlus className="w-4 h-4" />} Abrir solicitação
        </button>
      )}
    </div>
  );
};

const useMascotChat = () => {
  const [sid, setSid] = useState(() => localStorage.getItem(SID_KEY) || newSid());
  const [msgs, setMsgs] = useState([HELLO]);
  const [busy, setBusy] = useState(true);
  useEffect(() => {
    setBusy(true);
    api.get(`/school/mascot/history?session_id=${sid}`, { silent: true }).then((r) => setMsgs([HELLO, ...(r.data || [])])).catch(() => {}).finally(() => setBusy(false));
  }, [sid]);
  const add = (m) => setMsgs((l) => [...l, m]);
  const patchLast = (f) => setMsgs((l) => [...l.slice(0, -1), { ...l[l.length - 1], ...f }]);
  const send = async (text) => {
    if (busy) return;
    add({ role: "user", text }); add({ role: "bot", text: "", streaming: true }); setBusy(true);
    try {
      const res = await fetch(`${API}/school/mascot/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message: text }) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); patchLast({ text: e.detail || "Não consegui responder agora.", streaming: false }); return; }
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = ""; let acc = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const chunks = buf.split("\n\n"); buf = chunks.pop();
        for (const c of chunks) {
          if (!c.startsWith("data: ")) continue;
          const ev = JSON.parse(c.slice(6));
          if (ev.type === "delta") { acc += ev.content; patchLast({ text: acc }); }
          if (ev.type === "done") patchLast({ id: ev.id, text: ev.text, proposal: ev.proposal, streaming: false });
          if (ev.type === "error") patchLast({ text: ev.content, streaming: false });
        }
      }
    } catch { patchLast({ text: "Sem conexão agora. Tenta de novo?", streaming: false }); }
    finally { setBusy(false); }
  };
  const reset = () => { setSid(newSid()); setMsgs([HELLO]); };
  return { msgs, busy, send, reset, add };
};

const Panel = ({ onClose }) => {
  const { msgs, busy, send, reset, add } = useMascotChat();
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const submit = (e) => { e.preventDefault(); const t = text.trim(); if (!t || busy) return; setText(""); send(t); };
  return (
    <div className="fixed z-50 bottom-0 right-0 md:bottom-6 md:right-6 w-full md:w-[380px] h-[85dvh] md:h-[580px] flex flex-col rounded-t-3xl md:rounded-3xl overflow-hidden bg-slate-50 shadow-2xl shadow-indigo-950/30 border border-slate-200 mascot-panel-in" data-testid="school-mascot-panel">
      <div className="flex items-center gap-3 px-4 py-3 bg-[#1e3a8a] text-white">
        <MascotAvatar size="w-11 h-11" />
        <div className="flex-1 min-w-0">
          <p className="font-heading font-black text-[15px] leading-tight">{MASCOT_NAME}</p>
          <p className="text-[11px] text-sky-200">Consulta os dados da sua escola</p>
        </div>
        <button type="button" onClick={reset} data-testid="school-mascot-reset" title="Nova conversa" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><RotateCcw className="w-4 h-4" /></button>
        <button type="button" onClick={onClose} data-testid="school-mascot-close" aria-label="Fechar" className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="school-mascot-messages">
        {msgs.length <= 1 && <MascotHero src={MASCOT_THUMBS} />}
        {msgs.map((m, i) => (
          <React.Fragment key={i}>
            <Bubble from={m.role === "user" ? "user" : "bot"} testid={`school-mascot-msg-${m.role}`}>
              {m.streaming && !m.text ? <span className="inline-flex gap-1" data-testid="school-mascot-typing"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:120ms]" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:240ms]" /></span> : <span className="whitespace-pre-line">{visible(m.text)}</span>}
            </Bubble>
            {m.proposal && m.id && <Proposal p={m.proposal} msgId={m.id} onSent={(t) => add({ role: "bot", text: `Pronto! Seu pedido "${t}" chegou para a equipe AMC.` })} />}
          </React.Fragment>
        ))}
        {msgs.length <= 1 && (
          <div className="flex flex-wrap gap-1.5 pl-9" data-testid="school-mascot-suggestions">
            {SUGGESTIONS.map((s, i) => <button key={s} type="button" onClick={() => send(s)} data-testid={`school-mascot-suggestion-${i}`} className="px-3 py-1.5 rounded-full border border-[#7c3aed]/40 text-[#5b21b6] bg-violet-50 hover:bg-violet-100 text-[12px] font-bold transition-colors">{s}</button>)}
          </div>
        )}
        <div ref={end} />
      </div>
      <form onSubmit={submit} className="flex gap-2 p-3 border-t border-slate-200 bg-white" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={800} placeholder="Pergunte ao Maurilho..." data-testid="school-mascot-input"
          className="flex-1 h-10 px-3 rounded-xl border border-slate-300 text-[14px] focus:outline-none focus:ring-2 focus:ring-[#7c3aed]/40" />
        <button type="submit" disabled={busy || !text.trim()} data-testid="school-mascot-send" className="w-10 h-10 rounded-xl bg-[#7c3aed] text-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};

export default function SchoolMascotChat() {
  const [open, setOpen] = useState(false);
  if (open) return <Panel onClose={() => setOpen(false)} />;
  return <MascotLauncher onClick={() => setOpen(true)} testid="school-mascot-launcher" className="bottom-16 right-2 md:bottom-4 md:right-5" />;
}
